import {SECTIONS} from 'constants/NavConstants';
import navigateToURL from 'lib/navigateToURL';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import {StructuredData} from 'notebook/data-tables/DataTableConstants';
import notifications from 'notifications';
import {getStore} from 'redux-stores/Store';
import * as SearchUIReduxActions from 'search/SearchUIReduxActions';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import {Optional} from 'util/Types';
import * as StageRunActions from 'workflows/StageRunActions';

import * as WorkflowDAO from './dao/WorkflowDAO';
import {getWorkflowURL} from './util/WorkflowUtil';
import * as WorkflowVisualizationReduxActions from './visualization/WorkflowVisualizationReduxActions';
import {
  StageEntryDocument,
  StageFlowNode,
  StageRun,
  StageRunSample,
  StageRunSampleKey,
  Workflow,
  WorkflowDetails,
} from './WorkflowConstants';

export enum WorkflowActionType {
  WORKFLOW_WORKFLOW_DASHBOARD_SCREEN_OPEN = 'WORKFLOW_WORKFLOW_DASHBOARD_SCREEN_OPEN',
  WORKFLOW_WORKFLOW_DASHBOARD_TEMPLATES_SCREEN_OPEN = 'WORKFLOW_WORKFLOW_DASHBOARD_TEMPLATES_SCREEN_OPEN',
  WORKFLOW_WORKFLOW_DETAILS_SCREEN_OPEN = 'WORKFLOW_WORKFLOW_DETAILS_SCREEN_OPEN',
  WORKFLOW_STAGE_RUNS_FETCH_COMPLETED = 'WORKFLOW_STAGE_RUNS_FETCH_COMPLETED',
  WORKFLOW_STAGE_RUN_SAMPLES_FETCH_COMPLETED = 'WORKFLOW_STAGE_RUN_SAMPLES_FETCH_COMPLETED',
  WORKFLOW_STAGE_ENTRY_DETAILS_FETCH_COMPLETED = 'WORKFLOW_STAGE_ENTRY_DETAILS_FETCH_COMPLETED',
  WORKFLOW_WORKFLOW_FETCH_COMPLETED = 'WORKFLOW_WORKFLOW_FETCH_COMPLETED',
  WORKFLOW_WORKFLOW_DETAILS_FETCH_COMPLETED = 'WORKFLOW_WORKFLOW_DETAILS_FETCH_COMPLETED',
}

export function routeToWorkflow(workflowId: string): void {
  navigateToURL(getWorkflowURL(workflowId));
}

interface WorkflowDashboardScreenOpenAction {
  type: WorkflowActionType.WORKFLOW_WORKFLOW_DASHBOARD_SCREEN_OPEN;
}

export function openWorkflowDashboardScreen(): void {
  getStore().dispatch({
    type: WorkflowActionType.WORKFLOW_WORKFLOW_DASHBOARD_SCREEN_OPEN,
  });
}

interface WorkflowDashboardTemplatesScreenOpenAction {
  type: WorkflowActionType.WORKFLOW_WORKFLOW_DASHBOARD_TEMPLATES_SCREEN_OPEN;
}

export function openWorkflowDashboardTemplatesScreen(): void {
  getStore().dispatch({
    type: WorkflowActionType.WORKFLOW_WORKFLOW_DASHBOARD_TEMPLATES_SCREEN_OPEN,
  });
}

interface WorkflowDetailsScreenOpenAction {
  type: WorkflowActionType.WORKFLOW_WORKFLOW_DETAILS_SCREEN_OPEN;
  workflowId: string;
}

export function openWorkflowDetailsScreen(workflowId: string): void {
  getStore().dispatch({
    type: WorkflowActionType.WORKFLOW_WORKFLOW_DETAILS_SCREEN_OPEN,
    workflowId,
  });
  getStore().dispatch(WorkflowVisualizationReduxActions.reset());
}

interface WorkflowStageRunsFetchedAction {
  type: WorkflowActionType.WORKFLOW_STAGE_RUNS_FETCH_COMPLETED;
  stageRuns: Array<StageRun>;
  entryIdToStructuredData?: {[stageEntryId: string]: StructuredData};
}

export function stageRunsFetched(
  stageRuns: Array<StageRun>,
  entryIdToStructuredData?: {[stageEntryId: string]: StructuredData}
): void {
  getStore().dispatch({
    type: WorkflowActionType.WORKFLOW_STAGE_RUNS_FETCH_COMPLETED,
    stageRuns,
    entryIdToStructuredData,
  });
}

interface WorkflowStageRunSamplesFetchCompletedAction {
  type: WorkflowActionType.WORKFLOW_STAGE_RUN_SAMPLES_FETCH_COMPLETED;
  stageRunSamples: Array<StageRunSample>;
}

export function stageRunSamplesFetched(stageRunSamples: Array<StageRunSample>): void {
  getStore().dispatch({
    type: WorkflowActionType.WORKFLOW_STAGE_RUN_SAMPLES_FETCH_COMPLETED,
    stageRunSamples,
  });
}

interface WorkflowStageEntryDetailsFetchCompletedAction {
  type: WorkflowActionType.WORKFLOW_STAGE_ENTRY_DETAILS_FETCH_COMPLETED;
  stageEntry: StageEntryDocument;
  stageRuns: {[stageRunId: string]: StageRun};
  stageFlowNodes: {[stageFlowNodeId: string]: StageFlowNode};
  inputSamplesOrder: Array<StageRunSampleKey>;
  outputSamplesOrder: Array<StageRunSampleKey>;
  workflowStageOrderedStageRunIds: Array<string>;
}

// Calling this will cause the workflow dashboard to execute a new query to retrieve
// the list of workflows.
function invalidateWorkflowSearchCache(): void {
  getStore().dispatch(SearchUIReduxActions.forceRefreshSearch(SECTIONS.WORKFLOWS));
}

export function createWorkflow(
  {workflowTemplateVersionId, rootFolderId, name, prefix, fulfillments}: WorkflowDAO.CreateWorkflowParams,
  {onSuccess}: {onSuccess?: () => void} = {}
): void {
  LoadingScreen.show();
  WorkflowDAO.createWorkflow({workflowTemplateVersionId, rootFolderId, name, prefix, fulfillments})
    .done(({workflow}: WorkflowDAO.WorkflowResponse) => {
      invalidateWorkflowSearchCache();
      if (onSuccess) {
        onSuccess();
      }
      SidepanelActions.openSection(SECTIONS.WORKFLOWS);
      openWorkflowDetailsScreen(workflow.id);
    })
    .fail((res) => {
      notifications.notifyError(res);
    })
    .always(() => {
      LoadingScreen.hide();
    });
}

export function deleteWorkflow(workflowId: string): void {
  LoadingScreen.show();
  WorkflowDAO.deleteWorkflow(workflowId)
    .done(() => {
      invalidateWorkflowSearchCache();
      openWorkflowDashboardScreen();
    })
    .fail((res) => {
      notifications.notifyError(res);
    })
    .always(() => {
      LoadingScreen.hide();
    });
}

interface WorkflowFetchCompletedAction {
  type: WorkflowActionType.WORKFLOW_WORKFLOW_FETCH_COMPLETED;
  workflow: Workflow;
}

export function editWorkflowMetadata(
  workflowId: string,
  {
    name,
    description,
    rootFolderId,
    authorIds,
    onSuccess,
  }: WorkflowDAO.EditWorkflowMetadataParams & {onSuccess?: Optional<() => void>}
): void {
  LoadingScreen.show();
  WorkflowDAO.editWorkflowMetadata(workflowId, {name, description, rootFolderId, authorIds})
    .done(({workflow}: WorkflowDAO.WorkflowResponse) => {
      getStore().dispatch({
        type: WorkflowActionType.WORKFLOW_WORKFLOW_FETCH_COMPLETED,
        workflow,
      });
      if (onSuccess) {
        onSuccess();
      }
    })
    .fail((res) => {
      notifications.notifyError(res);
    })
    .always(() => {
      LoadingScreen.hide();
    });
}

export async function fetchWorkflowDetails(workflowId: string): Promise<void> {
  const workflowDetails = await WorkflowDAO.fetchWorkflowDetails(workflowId);
  const {stageRuns, entryIdToStructuredData} = workflowDetails;
  StageRunActions.dispatchStageRunsFetchedAndFetchMissingSamples({
    stageRuns: Object.values(stageRuns),
    entryIdToStructuredData,
  });
  getStore().dispatch({
    type: WorkflowActionType.WORKFLOW_WORKFLOW_DETAILS_FETCH_COMPLETED,
    ...workflowDetails,
  });
}

interface WorkflowDetailsFetchCompleted extends WorkflowDetails {
  type: WorkflowActionType.WORKFLOW_WORKFLOW_DETAILS_FETCH_COMPLETED;
}

export function initializeWorkflow({
  workflowId,
  initialStageRunParams,
}: WorkflowDAO.InitializeWorkflowParams): void {
  LoadingScreen.show();
  WorkflowDAO.initializeWorkflow({workflowId, initialStageRunParams})
    .done((workflowDetails: WorkflowDetails) => {
      invalidateWorkflowSearchCache();
      getStore().dispatch(WorkflowVisualizationReduxActions.reset());
      const {stageRuns, entryIdToStructuredData} = workflowDetails;
      StageRunActions.dispatchStageRunsFetchedAndFetchMissingSamples({
        stageRuns: Object.values(stageRuns),
        entryIdToStructuredData,
      });
      getStore().dispatch({
        type: WorkflowActionType.WORKFLOW_WORKFLOW_DETAILS_FETCH_COMPLETED,
        ...workflowDetails,
      });
    })
    .fail((res) => {
      notifications.notifyError(res);
    })
    .always(() => {
      LoadingScreen.hide();
    });
}

export type WorkflowAction =
  | WorkflowDashboardScreenOpenAction
  | WorkflowDashboardTemplatesScreenOpenAction
  | WorkflowDetailsScreenOpenAction
  | WorkflowFetchCompletedAction
  | WorkflowStageRunsFetchedAction
  | WorkflowStageRunSamplesFetchCompletedAction
  | WorkflowStageEntryDetailsFetchCompletedAction
  | WorkflowDetailsFetchCompleted;
